// CustodyEventDialogContext.tsx
import React, { createContext, useContext, useState } from "react";
import { CustodyEvent } from "../models/CustodyEvent";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../design/description-list";
import moment from "moment";
import { Button } from "../design/button";
import { useCreateEventDialog } from "./CreateEventDialogProvider";
import { useParticipant } from "../contexts/ParticipantContext";

interface Context {
  openDialog: (custodyEvent: CustodyEvent) => void;
  closeDialog: () => void;
  isOpen: boolean;
  custodyEvent: CustodyEvent | undefined;
}

const CustodyEventDialogContext = createContext<Context | undefined>(undefined);

export const CustodyEventDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { participants } = useParticipant();
  const { openDialog: openCreateEventDialog } = useCreateEventDialog();
  const [custodyEvent, setCustodyEvent] = useState<CustodyEvent | undefined>(
    undefined,
  );
  const [isOpen, setIsOpen] = useState(false);

  const openDialog = (custodyEvent: CustodyEvent) => {
    setCustodyEvent(custodyEvent);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setCustodyEvent(undefined);
  };

  return (
    <CustodyEventDialogContext.Provider
      value={{ openDialog, closeDialog, isOpen, custodyEvent }}
    >
      {custodyEvent && (
        <Dialog open={isOpen} onClose={closeDialog}>
          <DialogTitle>{custodyEvent.title}</DialogTitle>
          <DialogBody>
            <DescriptionList>
              <DescriptionTerm>Participant</DescriptionTerm>
              <DescriptionDetails>
                {participants.find((p) => p._id === custodyEvent.participantId)
                  ?.name || "Unknown Participant"}
              </DescriptionDetails>
              <DescriptionTerm>Start</DescriptionTerm>
              <DescriptionDetails>
                {moment(
                  new Date(
                    `${custodyEvent.startDate}T${custodyEvent.startTime}`,
                  ),
                ).format("LLL")}
              </DescriptionDetails>
              <DescriptionTerm>End</DescriptionTerm>
              <DescriptionDetails>
                {moment(
                  new Date(`${custodyEvent.endDate}T${custodyEvent.endTime}`),
                ).format("LLL")}
              </DescriptionDetails>
            </DescriptionList>
          </DialogBody>
          <DialogActions>
            <Button plain onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                openCreateEventDialog(
                  new Date(
                    `${custodyEvent.startDate}T${custodyEvent.startTime}`,
                  ),
                )
              }
            >
              Document Event
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {children}
    </CustodyEventDialogContext.Provider>
  );
};

export const useCustodyEventDialog = () => {
  const ctx = useContext(CustodyEventDialogContext);
  if (!ctx)
    throw new Error(
      "useCustodyEventDialog must be used inside CustodyEventDialogProvider",
    );
  return ctx;
};
